<template>
  <div class="mobile-draws-section">
    <div class="mobile-draws-header">
      <h2 class="mobile-section-title">1. Select Draws</h2>
    </div>
    
    <div class="mobile-selected-draws-section">
      <div class="mobile-selected-header" @click="toggleCollapsed">
        <div class="mobile-selected-content">
          <div v-if="selectedDrawDates.length === 0" class="mobile-no-selection">
            No draws selected
          </div>
          <div v-else class="mobile-selected-list">
            <span class="mobile-selected-text">{{ formattedSelectedDraws }}</span>
          </div>
        </div>
        <button
          class="mobile-collapse-btn"
          @click.stop="toggleCollapsed"
          :aria-label="collapsed ? 'Expand draws' : 'Collapse draws'"
        >
          <svg 
            class="mobile-arrow-icon" 
            :class="{ 'rotated': !collapsed }"
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
          >
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </button>
      </div>
      
      <div v-show="!collapsed" class="mobile-draws-container">
      <div class="mobile-draws-grid">
        <div
          v-for="(draw, index) in props.draws"
          :key="draw.event_id"
          class="mobile-draw-item"
          :class="{ 'selected': props.selectedDraws[index] }"
          @click="toggleDrawSelection(index)"
        >
          <div class="mobile-draw-content">
            <div class="mobile-draw-info">
              <span class="mobile-draw-date">{{ formatDate(new Date(draw.draw_date)) }}</span>
              <span class="mobile-draw-time">{{ formatTime(new Date(draw.draw_date)) }}</span>
            </div>
          </div>
          <div class="mobile-checkbox-container">
            <input 
              type="checkbox" 
              :checked="props.selectedDraws[index]" 
              @change="handleCheckboxChange(index, $event)"
              class="mobile-checkbox"
            />
            <div class="mobile-checkbox-custom"></div>
          </div>
        </div>
      </div>
      
      <!-- Quick Actions -->
      <div class="mobile-quick-actions">
        <button 
          class="mobile-action-btn secondary" 
          @click="selectAll"
          :disabled="allSelected"
        >
          Select All
        </button>
        <button 
          class="mobile-action-btn secondary" 
          @click="clearAll"
          :disabled="selectedCount <= 1"
        >
          Clear All
        </button>
      </div>
      
      <!-- See less text -->
      <div class="mobile-see-less">
        <span class="mobile-see-less-text" @click="toggleCollapsed">See less</span>
      </div>

      
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue';

interface Draw {
  event_id: string | number;
  draw_date: string;
}

interface Props {
  draws: Draw[];
  selectedDraws: boolean[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-selected-draws': [index: number, checked: boolean];
  'toggle-draw-selection': [index: number];
}>();

const collapsed = ref(true);

// Computed properties
const selectedCount = computed(() => {
  return props.selectedDraws.filter(Boolean).length;
});

const selectedDrawDates = computed(() => {
  return props.draws
    .filter((_, index) => props.selectedDraws[index])
    .map(draw => draw.draw_date);
});

const formattedSelectedDraws = computed(() => {
  return selectedDrawDates.value
    .map(drawDate => {
      const date = new Date(drawDate);
      return `${formatDate(date)} ${formatTime(date)}`;
    })
    .join(', ');
});

const allSelected = computed(() => {
  return props.selectedDraws.every(Boolean);
});

// Methods
const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
};

// Ensure at least one draw is always selected
const ensureMinimumSelection = () => {
  const selectedCount = props.selectedDraws.filter(Boolean).length;
  if (selectedCount === 0 && props.draws.length > 0) {
    // Select the first draw if none are selected
    emit('update-selected-draws', 0, true);
  }
};

// Watch for changes in selectedDraws to ensure minimum selection
watch(() => props.selectedDraws, () => {
  ensureMinimumSelection();
}, { immediate: true });

const toggleDrawSelection = (index: number) => {
  emit('toggle-draw-selection', index);
};

const handleCheckboxChange = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update-selected-draws', index, target.checked);
};

const selectAll = () => {
  props.draws.forEach((_, index) => {
    if (!props.selectedDraws[index]) {
      emit('update-selected-draws', index, true);
    }
  });
};

const clearAll = () => {
  // Only clear if there are multiple draws selected
  // Keep at least one draw selected
  const selectedIndices = props.selectedDraws
    .map((selected, index) => selected ? index : -1)
    .filter(index => index !== -1);
  
  if (selectedIndices.length > 1) {
    // Clear all except the first one
    selectedIndices.slice(1).forEach(index => {
      emit('update-selected-draws', index, false);
    });
  }
};

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
</script>

<script lang="ts">
export default {
  name: "MobileDrawsSection",
};
</script>

<style scoped>
.mobile-draws-section {
  background: var(--bg-secondary);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.mobile-draws-header {
  padding: 16px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.mobile-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.mobile-selected-draws-section {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.mobile-selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mobile-selected-header:hover {
  background: var(--bg-hover);
}

.mobile-selected-content {
  flex: 1;
}

.mobile-selected-draws {
  margin-top: 8px;
}

.mobile-no-selection {
  font-size: 13px;
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-selected-list {
  display: flex;
  align-items: center;
}

.mobile-selected-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.4;
}

.mobile-collapse-btn {
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  padding: 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mobile-collapse-btn:hover {
  background: var(--bg-hover);
  border-color: var(--accent-color);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.mobile-arrow-icon {
  width: 20px;
  height: 20px;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.mobile-arrow-icon.rotated {
  transform: rotate(180deg);
}

.mobile-draws-container {
  padding: 8px 10px;
}

.mobile-draws-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.mobile-draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-draw-item:hover {
  background: var(--bg-hover);
  border-color: var(--accent-color);
}

.mobile-draw-item.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.mobile-draw-content {
  flex: 1;
}

.mobile-draw-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mobile-draw-date {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.mobile-draw-item.selected .mobile-draw-date {
  color: white;
}

.mobile-draw-time {
  font-size: 12px;
  color: var(--text-secondary);
}

.mobile-draw-item.selected .mobile-draw-time {
  color: rgba(255, 255, 255, 0.8);
}

.mobile-checkbox-container {
  position: relative;
}

.mobile-checkbox {
  opacity: 0;
  position: absolute;
  width: 0;
  height: 0;
}

.mobile-checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-checkbox:checked + .mobile-checkbox-custom {
  background: white;
  border-color: white;
}

.mobile-checkbox:checked + .mobile-checkbox-custom::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 8px;
  border: solid var(--accent-color);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.mobile-quick-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}

.mobile-action-btn {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mobile-action-btn:hover:not(:disabled) {
  border-color: var(--accent-color);
  background: var(--accent-color);
  color: white;
}

.mobile-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-action-btn.secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.mobile-action-btn.secondary:hover:not(:disabled) {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.mobile-see-less {
  display: flex;
  justify-content: center;
  padding-top: 12px;
}

.mobile-see-less-text {
  color: var(--accent-color);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.2s ease;
}

.mobile-see-less-text:hover {
  opacity: 0.8;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-draws-header {
    padding: 12px 16px;
  }
  
  .mobile-section-title {
    font-size: 14px;
  }
  
  .mobile-selected-header {
    padding: 10px 12px;
  }
  
  .mobile-selected-text {
    font-size: 12px;
  }
  
  .mobile-draw-item {
    padding: 10px 12px;
  }
  
  .mobile-draw-date {
    font-size: 13px;
  }
  
  .mobile-draw-time {
    font-size: 11px;
  }
  
  .mobile-quick-actions {
    flex-direction: row;
    gap: 6px;
  }
  
  .mobile-action-btn {
    padding: 8px 10px;
    font-size: 12px;
  }
  
  .mobile-see-less-text {
    font-size: 13px;
  }
  
  .mobile-collapse-btn {
    padding: 10px;
  }
  
  .mobile-arrow-icon {
    width: 18px;
    height: 18px;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .mobile-draws-section {
    background: var(--bg-secondary);
  }
  
  .mobile-draws-header {
    background: var(--bg-tertiary);
  }
  
  .mobile-draw-item {
    background: var(--bg-primary);
  }
  
  .mobile-checkbox-custom {
    background: var(--bg-primary);
  }
}
</style>
